import React, { useEffect } from "react";
import { getApiBaseURL } from "../../utils/api";
import { formatDate } from "../../utils/dateFormatter";
import secureAxios from "../../utils/secureAxios";

const ViewMonthlyReport = ({ report, onClose }) => {
  useEffect(() => {
    // No console logging
  }, [report]);

  // Check for missing critical props early
  if (!report?.report_id || !report?.tenant_id) {
    return (
      <p className="text-danger">
        Missing report or tenant ID. Cannot load report preview.
      </p>
    );
  }

  const handlePreview = async () => {
    const { report_id, tenant_id } = report || {};

    if (!report_id || !tenant_id) {
      alert("Cannot preview PDF: missing report ID or tenant ID.");
      return;
    }

    try {
      const apiBase = getApiBaseURL();
      const previewUrl = `${apiBase}/api/documents/preview/source/report/${report_id}?tenant_id=${tenant_id}`;

      // Log audit for viewing PDF
      try {
        await secureAxios.post(`${apiBase}/api/audit-logs/log`, {
          action: "VIEW_MONTHLY_REPORT_PDF",
          entity_type: "MONTHLY_REPORT",
          entity_id: report_id,
          entity_identifier: `PDF Preview for Report ${
            report.report_number || report_id
          }`,
          purpose_of_use: "TREATMENT",
          contains_phi: true,
          phi_types: ["medical_information", "service_records"],
          data_sensitivity_level: "high",
          fields_accessed: ["full_report_pdf"],
        });
      } catch (auditErr) {
        // Silent fail for audit logging
      }

      window.open(previewUrl, "_blank");
    } catch (err) {
      alert("Failed to open PDF preview.");
    }
  };

  // Function to get status badge color
  const getStatusBadgeClass = (status) => {
    if (!status) return "badge bg-secondary";

    const statusLower = status.toLowerCase();
    if (statusLower === "completed") return "badge bg-success";
    if (statusLower === "in progress") return "badge bg-primary";
    if (statusLower === "pending") return "badge bg-warning text-dark";
    if (statusLower === "cancelled") return "badge bg-danger";
    return "badge bg-secondary";
  };

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <h5 className="mb-0">Monthly Report Details</h5>
          <span className="badge bg-warning text-dark ms-3">
            <i className="bi bi-shield-lock me-1"></i>
            PHI - Protected
          </span>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="card-body">
        <div className="alert alert-info mb-4">
          <i className="bi bi-info-circle me-2"></i>
          <strong>Privacy Notice:</strong> This report contains Protected Health
          Information (PHI). Your access to this report has been logged. Only
          share with authorized personnel.
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <p>
              <strong>Report #:</strong> {report.report_number || "—"}
            </p>
            <p>
              <strong>Consumer:</strong> {report.consumer_name || "—"}
            </p>
            <p>
              <strong>Authorization:</strong>{" "}
              {report.authorization_number || "—"}
            </p>
            <p>
              <strong>Service:</strong> {report.service_name || "—"}
            </p>
          </div>
          <div className="col-md-6">
            <p>
              <strong>Date of Report:</strong>{" "}
              {formatDate(report.date_of_report || "—")}
            </p>
            <p>
              <strong>Estimated Completion:</strong>{" "}
              {formatDate(report.estimated_completion_date || "—")}
            </p>
            <p>
              <strong>Status:</strong>{" "}
              <span className={getStatusBadgeClass(report.status_name)}>
                {report.status_name || "Pending"}
              </span>
            </p>
          </div>
        </div>

        <hr />

        <h6 className="mb-3">
          <i className="bi bi-file-text me-2"></i>
          Report Content
        </h6>

        <div className="bg-light p-3 rounded mb-3">
          <p className="mb-2">
            <strong>Goal:</strong> {report.goal || "—"}
          </p>
          <p className="mb-2">
            <strong>Objectives:</strong> {report.objectives || "—"}
          </p>
          <p className="mb-2">
            <strong>Results:</strong> {report.results || "—"}
          </p>
          <p className="mb-2">
            <strong>Productivity:</strong> {report.productivity || "—"}
          </p>
          <p className="mb-2">
            <strong>Assessment:</strong> {report.assessment || "—"}
          </p>
          <p className="mb-2">
            <strong>Consumer Assessment:</strong>{" "}
            {report.consumer_assessment || "—"}
          </p>
          <p className="mb-2">
            <strong>Post Test:</strong> {report.post_test || "—"}
          </p>
          <p className="mb-0">
            <strong>Recommendations:</strong> {report.recommendations || "—"}
          </p>
        </div>

        <hr />
        <h5>
          <i className="bi bi-pen me-2"></i>
          Signatures
        </h5>

        <div className="alert alert-secondary">
          <i className="bi bi-check2-circle me-2"></i>
          Electronic signatures captured below are legally binding under the
          E-Sign Act.
        </div>

        {report.consumer_signature_base64 && (
          <div className="mb-3">
            <label>
              <strong>Consumer Signature:</strong>
            </label>
            <div>
              <img
                src={report.consumer_signature_base64}
                alt="Consumer Signature"
                style={{
                  border: "1px solid #ccc",
                  maxWidth: 500,
                  maxHeight: 150,
                }}
              />
            </div>
            <strong>Consumer Signature Date:</strong>{" "}
            {formatDate(report.consumer_signature_date || "—")}
          </div>
        )}

        {report.staff_signature_base64 && (
          <div className="mb-3">
            <label>
              <strong>Staff Signature:</strong>
            </label>
            <div>
              <img
                src={report.staff_signature_base64}
                alt="Staff Signature"
                style={{
                  border: "1px solid #ccc",
                  maxWidth: 500,
                  maxHeight: 150,
                }}
              />
            </div>
            <strong>Staff Signature Date:</strong>{" "}
            {formatDate(report.staff_signature_date || "—")}
          </div>
        )}

        {report.other_signature_base64 && (
          <div className="mb-3">
            <label>
              <strong>Other Signature:</strong>
            </label>
            <div>
              <img
                src={report.other_signature_base64}
                alt="Other Signature"
                style={{
                  border: "1px solid #ccc",
                  maxWidth: 500,
                  maxHeight: 150,
                }}
              />
            </div>
            <strong>Other Signature Date:</strong>{" "}
            {formatDate(report.other_signature_date || "—")}
          </div>
        )}

        <button
          className="btn btn-outline-primary mt-3"
          onClick={handlePreview}
        >
          <i className="bi bi-file-pdf me-2"></i>
          Preview Secure PDF
        </button>

        <div className="text-muted mt-3">
          <small>
            <i className="bi bi-lock me-1"></i>
            PDF documents are encrypted and access is logged for HIPAA
            compliance.
          </small>
        </div>
      </div>
    </div>
  );
};

export default ViewMonthlyReport;
